import type { ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardRouter from './components/DashboardRouter';
import AppShell from './layout/AppShell';
import { uiFlags } from './config/uiFlags';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ManufacturerDashboard from './pages/ManufacturerDashboard';
import DistributorDashboard from './pages/DistributorDashboard';
import HierarchyConfigPage from './pages/HierarchyConfigPage';
import UserApprovalsPage from './pages/UserApprovalsPage';
import UserManagementPage from './pages/UserManagementPage';
import CreateBatchPage from './pages/CreateBatchPage';
import BatchDetailsPage from './pages/BatchDetailsPage';
import ProductInventoryPage from './pages/ProductInventoryPage';
import ProductInfoManagementPage from './pages/ProductInfoManagementPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import ApiAccessDashboard from './pages/ApiAccessDashboard';
import BatchTitleSettingsPage from './pages/BatchTitleSettingsPage';
import SettingsCenterPage from './pages/SettingsCenterPage';
import QRVerificationPage from './pages/QRVerificationPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminCreditManagementPage from './pages/AdminCreditManagementPage';
import AdminOperationCostsPage from './pages/AdminOperationCostsPage';
import AdminAnomalyPolicyPage from './pages/AdminAnomalyPolicyPage';
import AdminAnomalyFlagsPage from './pages/AdminAnomalyFlagsPage';
import AdminTrialManagementPage from './pages/AdminTrialManagementPage';
import AdminCompliancePage from './pages/AdminCompliancePage';
import AdminRecallManagementPage from './pages/AdminRecallManagementPage';
import AdminRolesPermissionsPage from './pages/AdminRolesPermissionsPage';
import ManufacturerAnomalyFlagsPage from './pages/ManufacturerAnomalyFlagsPage';
import VerificationLogsPage from './pages/VerificationLogsPage';
import ConsumptionLogsPage from './pages/ConsumptionLogsPage';
import QRLifecyclePage from './pages/QRLifecyclePage';
import ProductInfoAuditPage from './pages/ProductInfoAuditPage';
import PackagingConfigPage from './pages/PackagingConfigPage';
import PackageVerificationPage from './pages/PackageVerificationPage';
import PackageAssignmentPage from './pages/PackageAssignmentPage';
import DealerPackageAcceptancePage from './pages/DealerPackageAcceptancePage';
import PaymentPage from './pages/PaymentPage';
import EnhancedRegistrationPage from './pages/EnhancedRegistrationPage';
import RegistrationPendingPage from './pages/RegistrationPendingPage';
import OperationManagersPage from './pages/OperationManagersPage';
import ManufacturerCreditManagementPage from './pages/ManufacturerCreditManagementPage';
import ManufacturerIssueReportsPage from './pages/ManufacturerIssueReportsPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import MasterServiceAgreementPage from './pages/MasterServiceAgreementPage';
import DataProcessingAgreementPage from './pages/DataProcessingAgreementPage';
import ApplicationNotePage from './pages/ApplicationNotePage';
import CopiedLabelsPage from './pages/CopiedLabelsPage';
import TalkToUsPage from './pages/TalkToUsPage';
import LearnHowPage from './pages/LearnHowPage';
import ContactSalesPage from './pages/ContactSalesPage';
import { TrialRegistrationPage } from './pages/TrialRegistrationPage';
import BatchCreationGuidePage from './pages/BatchCreationGuidePage';
import AdminIssueReportsPage from './pages/AdminIssueReportsPage';

function ShellContent({ children }: { children: ReactNode }) {
  if (!uiFlags.enableEnterpriseShell) {
    return <>{children}</>;
  }
  return <AppShell>{children}</AppShell>;
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/trial-registration" element={<TrialRegistrationPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify" element={<QRVerificationPage />} />
          <Route path="/verify/:token" element={<QRVerificationPage />} />
          <Route path="/vf" element={<QRVerificationPage />} />
          <Route path="/vf/:token" element={<QRVerificationPage />} />
          <Route path="/package/:qrToken" element={<PackageVerificationPage />} />
          <Route path="/dealer/accept/:qrToken" element={<DealerPackageAcceptancePage />} />
          <Route path="/application-note" element={<ApplicationNotePage />} />
          <Route path="/learn/copied-labels" element={<CopiedLabelsPage />} />
          <Route path="/talk-to-us" element={<TalkToUsPage />} />
          <Route path="/learn-how" element={<LearnHowPage />} />
          <Route path="/contact-sales" element={<ContactSalesPage />} />
          
          {/* Legal Pages */}
          <Route path="/terms" element={<TermsOfServicePage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/msa" element={<MasterServiceAgreementPage />} />
          <Route path="/data-processing" element={<DataProcessingAgreementPage />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardRouter />
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturer"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <ManufacturerDashboard />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/enhanced-registration"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <EnhancedRegistrationPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/registration-pending"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <RegistrationPendingPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          {/* Manufacturer-only routes */}
          <Route
            path="/hierarchy/config"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <HierarchyConfigPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/approvals"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <UserApprovalsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/users/manage"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <UserManagementPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/operations-managers"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <OperationManagersPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/create-batch"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <CreateBatchPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/guides/batch-creation"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <BatchCreationGuidePage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <SettingsCenterPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings/batch-title"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <BatchTitleSettingsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/batch/:batchId"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <BatchDetailsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products/batch/:batchId/manage"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <ProductInfoManagementPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/products"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <ProductInventoryPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/distributor"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <DistributorDashboard />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <AnalyticsDashboard />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/verification-logs"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <VerificationLogsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/consumption-logs"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <ConsumptionLogsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/qr-lifecycle"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <QRLifecyclePage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/api-access"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <ApiAccessDashboard />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/anomaly-flags"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <ManufacturerAnomalyFlagsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/packaging/config/:batchId"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <PackagingConfigPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/package-assignments"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <PackageAssignmentPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturer/payment"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <PaymentPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturer/credit-management"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <ManufacturerCreditManagementPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturer/issue-reports"
            element={
              <ProtectedRoute requireManufacturer>
                <ShellContent>
                  <ManufacturerIssueReportsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/manufacturer/payment-history"
            element={
              <ProtectedRoute requireManufacturerOnly>
                <ShellContent>
                  <PaymentPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          
          {/* Admin-only routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <AdminDashboard />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/credit-management"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <AdminCreditManagementPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/operation-costs"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <AdminOperationCostsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/anomaly/policy"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <AdminAnomalyPolicyPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/anomaly/flags"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <AdminAnomalyFlagsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/verification-logs"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <VerificationLogsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/consumption-logs"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <ConsumptionLogsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/qr-lifecycle"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <QRLifecyclePage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/product-info-audit"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <ProductInfoAuditPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/compliance"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <AdminCompliancePage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/recall-management"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <AdminRecallManagementPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/roles-permissions"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <AdminRolesPermissionsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/trial-management"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <AdminTrialManagementPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/issue-reports"
            element={
              <ProtectedRoute requireAdmin>
                <ShellContent>
                  <AdminIssueReportsPage />
                </ShellContent>
              </ProtectedRoute>
            }
          />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </ErrorBoundary>
  );
}

function NotFound() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-900 mb-4">404</h1>
        <p className="text-xl text-gray-600 mb-4">Page not found</p>
        <a
          href="/login"
          className="text-blue-600 hover:text-blue-700 font-medium"
        >
          Go to Login
        </a>
      </div>
    </div>
  );
}

export default App;
